import React, { useCallback, useEffect, useState } from "react";
import { Col, Container } from "reactstrap";
import { fetchCustomerList, resetCustomerPhoneNumber } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { makeStyles } from "@material-ui/core/styles";
import { daysToToday } from "utils/helpers/functions";
import { customDateFormatWithTime, textFormatter } from "components/Common/formatter";
import CustomLink from "components/Common/buttons/CustomLink";
import ButtonGroup from "components/Common/buttons/ButtonGroup";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import clearIcon from "assets/images/svg/clear-white.svg";

const useStyles = makeStyles({
  totalItem: {
    textAlign: "right",
    top: -43,
    position: "relative",
    zIndex: 1,
  },
});

const ResetPhone = () => {
  const classes = useStyles();
  const [isLoading, setLoader] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [activeItem, setActiveItem] = useState(null);

  useEffect(() => {
    handleSearch();
  }, []);

  const resetPhoneNumber = useCallback(() => {
    closeConfirmationModal();
    setLoader(true);
    resetCustomerPhoneNumber(activeItem)
      .then(res => {
        setLoader(false);
        successMessage("Customer phone number reset successfully");
        handleSearch();
      })
      .catch(err => {
        setLoader(false);
        handleSearch();
        defaultNgiErrorMethod(err);
      });
  }, [activeItem]);

  const onListLoaded = useCallback(res => {
    const {
      result: {
        items,
        meta: { totalCount },
      },
    } = res?.data;
    if (items?.length === 0) {
      warnMessage("Customers were not found");
    }
    setTotalCustomers(totalCount || 0);
    setCustomerList(res?.data || null);
    setLoader(false);
  }, []);

  const openConfirmationModal = useCallback(data => setActiveItem(data.id), []);
  const closeConfirmationModal = useCallback(() => setActiveItem(null), []);

  const handleSearch = (filterOptions = {}) => {
    setLoader(true);

    fetchCustomerList({
      depot: "TRAINING DEPOT",
      status: "Repossessed",
      ...filterOptions,
    })
      .then(res => onListLoaded(res))
      .catch(err => {
        setLoader(false);
        setCustomerList(null);
        defaultNgiErrorMethod(err);
      });
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Col lg={12}>
            <>
              <NGITable
                isLoading={isLoading}
                columns={resetPhoneListColumns(openConfirmationModal)}
                data={customerList}
                updateList={handleSearch}
              />
              {!!totalCustomers && totalCustomers > 0 && (
                <div className={classes.totalItem}>Total number of customers = {totalCustomers}</div>
              )}
            </>
          </Col>
        </Container>
      </div>
      {!!activeItem && (
        <ConfirmationModal
          description="Please confirm the customer primary phone number will be changed to - 00000000"
          onCancel={closeConfirmationModal}
          onConfirm={resetPhoneNumber}
        />
      )}
    </>
  );
};

export default ResetPhone;

const resetPhoneListColumns = onOpenResetModal => [
  {
    title: "Customer ID",
    dataIndex: "id",
    render: textFormatter,
  },
  {
    title: "Full Name",
    dataIndex: "",
    render: row => {
      const fullName = `${row.firstName} ${row.lastName}`;
      return textFormatter(fullName);
    },
  },
  {
    title: "TSR",
    dataIndex: "technician",
    render: (cellContent, row) => {
      const { installationDate, growthTechnician, technician, type } = row;

      const ageAsCustomer = daysToToday(installationDate);
      const technicianEmail =
        type === "Commercial" ? technician ?? growthTechnician : ageAsCustomer > 30 ? technician : growthTechnician;

      return technicianEmail ? (
        <CustomLink underlined target="_blank" to={`/user/${technicianEmail}`}>
          {technicianEmail}
        </CustomLink>
      ) : (
        textFormatter(technicianEmail)
      );
    },
  },
  {
    title: "Phone",
    dataIndex: "phone",
    render: textFormatter,
  },
  {
    title: "Secondary Phone",
    dataIndex: "secondaryPhone",
    render: textFormatter,
  },
  {
    title: "Category",
    dataIndex: "type",
    render: textFormatter,
  },
  {
    title: "Installation Date",
    dataIndex: "installationDate",
    render: cellContent => textFormatter(customDateFormatWithTime(cellContent)),
  },
  {
    title: "Repossession Date",
    dataIndex: "terminatedAt",
    render: cellContent => textFormatter(customDateFormatWithTime(cellContent)),
  },

  {
    title: "",
    dataIndex: "",
    render: cellContent => (
      <ButtonGroup
        handleClick={() => {
          onOpenResetModal(cellContent);
        }}
        tooltipTitle="Reset"
        icon={clearIcon}
      />
    ),
  },
];
